import { BabyWrappedIcon, InformationIcon } from '@navikt/aksel-icons';
import { ContextDataType, useContextGetData } from 'appData/PlanleggerDataContext';
import { usePlanleggerNavigator } from 'appData/usePlanleggerNavigator';
import { useStepData } from 'appData/useStepData';
import { PlanleggerStepPage } from 'components/page/PlanleggerStepPage';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFamiliehendelsedato } from 'steps/oppsummering/expansion-cards/BarnehageplassOppsummering';
import { erAlenesøker as erAlene } from 'utils/HvemPlanleggerUtils';
import { erBarnetAdoptert, erBarnetFødt } from 'utils/barnetUtils';
import { Uttaksdata } from 'utils/uttakUtils';

import { BodyLong, Heading, Link, VStack } from '@navikt/ds-react';

import { links } from '@navikt/fp-constants';
import { OmBarnetPlanlegger } from '@navikt/fp-types';
import { IconCircleWrapper, Infobox, StepButtons } from '@navikt/fp-ui';
import { beregnBarnehagestartDato, useScrollBehaviour } from '@navikt/fp-utils';
import { notEmpty } from '@navikt/fp-validation';

export const barnehagestartDato = (barnet: OmBarnetPlanlegger): string | undefined => {
    if (erBarnetAdoptert(barnet)) {
        return undefined;
    }

    const dato = erBarnetFødt(barnet) ? barnet.fødselsdato : barnet.termindato;
    return beregnBarnehagestartDato(dato);
};

interface Props {
    uttaksdata?: Uttaksdata;
}

export const BarnehageplassSteg = ({ uttaksdata }: Props) => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    useScrollBehaviour();

    const barnet = notEmpty(useContextGetData(ContextDataType.OM_BARNET));
    const hvemPlanlegger = notEmpty(useContextGetData(ContextDataType.HVEM_PLANLEGGER));
    const erAlenesøker = erAlene(hvemPlanlegger);
    const antallBarn = barnet.antallBarn;
    const sluttdato = uttaksdata?.sluttdatoPeriode2 || uttaksdata?.sluttdatoPeriode1;

    return (
        <PlanleggerStepPage steps={stepConfig} goToStep={navigator.goToNextStep}>
            <VStack gap="space-32">
                <Heading size="medium" level="2">
                    <FormattedMessage id="BarnehageplassSteg.Tittel" />
                </Heading>
                <VStack gap="space-20">
                    <BodyLong>
                        <FormattedMessage
                            id="BarnehageplassSteg.KommuneTekstDeg"
                            values={{ erAlenesøker, antallBarn }}
                        />
                    </BodyLong>
                    <Infobox
                        header={
                            erBarnetAdoptert(barnet) ? (
                                <FormattedMessage
                                    id="BarnehageplassSteg.DatoTittel"
                                    values={{
                                        dato: intl.formatDate(sluttdato, {
                                            month: 'long',
                                            year: 'numeric',
                                        }),
                                        erAlenesøker,
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="BarnehageplassSteg.DatoTittel"
                                    values={{
                                        dato: intl.formatDate(barnehagestartDato(barnet), {
                                            month: 'long',
                                            year: 'numeric',
                                        }),
                                        erAlenesøker,
                                    }}
                                />
                            )
                        }
                        color="blue"
                        icon={
                            <IconCircleWrapper color="lightBlue" size="medium">
                                <BabyWrappedIcon
                                    height={24}
                                    width={24}
                                    color="var(--ax-brand-blue-700)"
                                    fontSize="1.5rem"
                                    aria-hidden
                                />
                            </IconCircleWrapper>
                        }
                    >
                        <BodyLong>
                            <FormattedMessage
                                id="BarnehageplassSteg.DatoTekst"
                                values={{
                                    a: (msg) => (
                                        <Link inlineText href={links.barnehageloven} rel="noreferrer" target="_blank">
                                            {msg}
                                        </Link>
                                    ),
                                    dato: intl.formatDate(getFamiliehendelsedato(barnet), {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    }),
                                    antallBarn,
                                    erAlenesøker,
                                    erFødt: erBarnetFødt(barnet),
                                    erAdopsjon: erBarnetAdoptert(barnet),
                                }}
                            />
                        </BodyLong>
                    </Infobox>
                    <Infobox
                        header={<FormattedMessage id="BarnehageplassSteg.BarnehageTittel" />}
                        icon={
                            <InformationIcon
                                height={24}
                                width={24}
                                color="var(--ax-bg-neutral-strong)"
                                fontSize="1.5rem"
                                aria-hidden
                            />
                        }
                        color="gray"
                    >
                        <BodyLong>
                            <FormattedMessage id="BarnehageplassSteg.BarnehageTekst" values={{ erAlenesøker }} />
                        </BodyLong>
                    </Infobox>
                </VStack>
                <VStack gap="space-80">
                    <VStack>
                        <StepButtons
                            nextButtonOnClick={navigator.goToNextDefaultStep}
                            goToPreviousStep={navigator.goToPreviousDefaultStep}
                            useSimplifiedTexts
                        />
                    </VStack>
                </VStack>
            </VStack>
        </PlanleggerStepPage>
    );
};
